import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Button from '@/components/CustomButtons/Button';
import CustomInput from '@/components/CustomInput/CustomInput';
import loader from '../../assets/img/loader.svg';
import { toast } from 'react-toastify';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PUBLIC_ASSETS_BASE_URL } from '@/config';
import { Delete } from '@mui/icons-material';

import {
  getBusinessUnit,
  createBusinessUnit,
  updateBusinessUnit,
} from '@/api/clients/business-units/business-units.api';
import { FacilitiesApi } from '@/api/clients/facilities/facilities.api';
import isAllowedForFacility from '@/utility';

export default function CreateOrEditBusinessUnit() {
  const { t } = useTranslation();
  // useNavigate
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [message] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [formInputs, setFormInputs] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    logo: '',
    logoPreview: '',
    identifiers: [],
    facilityId: '',
  });

  useEffect(() => {
    const fetchBusinessUnit = async () => {
      if (!id) return;
      const businessUnit = await getBusinessUnit(id);
      setFormInputs({
        name: businessUnit.name,
        email: businessUnit.email,
        phone: businessUnit.phone,
        address: businessUnit.address,
        city: businessUnit.city,
        state: businessUnit.state,
        zipcode: businessUnit.zipcode,
        logoPreview: businessUnit.logo
          ? PUBLIC_ASSETS_BASE_URL + businessUnit.logo
          : null,
        identifiers: businessUnit.identifiers,
        facilityId: businessUnit.facility_id || '',
      });
    };

    fetchBusinessUnit();
  }, [id]);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const facilitiesData = await FacilitiesApi.getFacilitiesForForms();
        setFacilities(facilitiesData);
      } catch (error) {
        console.error('Error fetching facilities:', error);
        setFacilities([]);
      }
    };

    fetchFacilities();
  }, []);

  const handleInputChange = (e) => {
    setFormInputs({
      ...formInputs,
      [e.target.name]: e.target.value,
    });
  };

  const handleFacilityChange = (event) => {
    setFormInputs({
      ...formInputs,
      facilityId: event.target.value,
    });
  };

  const handleLogoUpload = (e) => {
    setFormInputs({
      ...formInputs,
      logoPreview: URL.createObjectURL(e.target.files[0]),
      logo: e.target.files[0],
    });
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      if (loading) return;
      setLoading(true);
      if (!formInputs.name || !formInputs.logoPreview) {
        toast.error(t('businessUnits.validation.nameAndLogoRequired'));
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('name', formInputs.name);
      formData.append('email', formInputs.email);
      formData.append('phone', formInputs.phone);
      formData.append('address', formInputs.address);
      formData.append('city', formInputs.city);
      formData.append('state', formInputs.state);
      formData.append('zipcode', formInputs.zipcode);
      formData.append('identifiers', JSON.stringify(formInputs.identifiers));
      formData.append('facilityId', formInputs.facilityId);

      if (formInputs.logo)
        formData.append('logo', formInputs.logo, formInputs.logo.name);

      if (id) {
        await updateBusinessUnit(id, formData);
        setLoading(false);
        navigate('/facility/business-units');
        return;
      }

      await createBusinessUnit(formData);
      setLoading(false);

      navigate('/facility/business-units');
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const addIdentifier = () => {
    setFormInputs({
      ...formInputs,
      identifiers: [...formInputs.identifiers, ''],
    });
  };

  const onIdentifierChange = (e, index) => {
    const newIdentifiers = formInputs.identifiers.map((identifier, i) =>
      i === index ? e.target.value : identifier,
    );
    setFormInputs({
      ...formInputs,
      identifiers: newIdentifiers,
    });
  };

  const removeIdentifier = (index) => {
    const newIdentifiers = formInputs.identifiers.filter(
      (identifier, i) => i !== index,
    );
    setFormInputs({
      ...formInputs,
      identifiers: newIdentifiers,
    });
  };

  const removeLogo = () => {
    setFormInputs({
      ...formInputs,
      logo: '',
      logoPreview: '',
    });
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color='primary'>
                <h4>
                  {id
                    ? t('businessUnits.form.editTitle')
                    : t('businessUnits.form.createTitle')}
                </h4>
              </CardHeader>
              <CardBody>
                {loading && (
                  <div className='loaders'>
                    <img src={loader} />
                  </div>
                )}

                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <div className='custom-select'>
                      <FormControl variant='standard'>
                        <InputLabel id='facility-select-label'>
                          {t('businessUnits.form.facilityLabel', 'Facility')}
                        </InputLabel>
                        <Select
                          labelId='facility-select-label'
                          value={formInputs.facilityId}
                          onChange={handleFacilityChange}
                          name='facilityId'
                          disabled={!!id}
                          className='full-width-elem'>
                          <MenuItem value=''>
                            <em>
                              {t(
                                'businessUnits.form.selectFacility',
                                'Select a facility',
                              )}
                            </em>
                          </MenuItem>
                          {facilities.map((facility) => (
                            <MenuItem
                              disabled={
                                !isAllowedForFacility({
                                  parentKey: 'business_units',
                                  childKey: 'business_units',
                                  permission: 'write',
                                  facilityId: facility.id,
                                })
                              }
                              key={facility.id}
                              value={facility.id}>
                              {facility.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      type='text'
                      labelText={t('businessUnits.form.nameLabel')}
                      onChange={handleInputChange}
                      value={formInputs.name}
                      id='name'
                      name='name'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('businessUnits.form.emailLabel')}
                      id='email'
                      name='email'
                      onChange={handleInputChange}
                      value={formInputs.email}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('businessUnits.form.phoneLabel')}
                      id='phone'
                      name='phone'
                      onChange={handleInputChange}
                      value={formInputs.phone}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('businessUnits.form.addressLabel')}
                      id='autocomplete'
                      name='address'
                      onChange={handleInputChange}
                      value={formInputs.address}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('businessUnits.form.cityLabel')}
                      id='city'
                      name='city'
                      onChange={handleInputChange}
                      value={formInputs.city}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('businessUnits.form.stateLabel')}
                      id='state'
                      name='state'
                      onChange={handleInputChange}
                      value={formInputs.state}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('businessUnits.form.zipcodeLabel')}
                      id='zipcode'
                      name='zipcode'
                      onChange={handleInputChange}
                      value={formInputs.zipcode}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}></GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <GridContainer>
                      {formInputs.identifiers.map((identifier, index) => (
                        <GridItem xs={12} sm={12} md={12} key={index}>
                          <CustomInput
                            onChange={(e) => onIdentifierChange(e, index)}
                            labelText={t('businessUnits.form.identifierLabel', {
                              number: index + 1,
                            })}
                            formControlProps={{}}
                            value={identifier}
                          />
                          <Delete
                            onClick={() => removeIdentifier(index)}
                            style={{ cursor: 'pointer' }}
                          />
                        </GridItem>
                      ))}
                    </GridContainer>
                    <GridItem xs={12} sm={12} md={6} style={{ padding: 0 }}>
                      <Button color='primary' onClick={addIdentifier}>
                        {t('businessUnits.form.addIdentifierButton')}
                      </Button>
                    </GridItem>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <div className='custom-upload m-b-30 m-t-27'>
                      <label htmlFor='logo'>
                        {t('businessUnits.form.uploadLogoLabel')}
                      </label>
                      <br />
                      <label htmlFor='logo' className='custom-upload-btn'>
                        <span>{t('businessUnits.form.browseButton')}</span>
                        <input
                          id='logo'
                          name='logo'
                          type='file'
                          accept='.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp'
                          style={{ display: 'none' }}
                          onChange={handleLogoUpload}
                        />
                      </label>
                    </div>
                    {formInputs.logoPreview ? (
                      <div className='preview-image' width='130'>
                        <img src={formInputs.logoPreview} width='130' />
                      </div>
                    ) : (
                      ''
                    )}

                    {formInputs.logoPreview ? (
                      <span
                        className='material-icons'
                        style={{ cursor: 'pointer' }}
                        onClick={() => removeLogo()}>
                        delete
                      </span>
                    ) : (
                      ''
                    )}
                  </GridItem>
                </GridContainer>
                {message && (
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <p style={{ color: 'red', padding: '10px 0' }}>
                        {message}
                      </p>
                    </GridItem>
                  </GridContainer>
                )}
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <Button color='primary' type='submit'>
                      {id
                        ? t('common.buttons.save')
                        : t('common.buttons.create')}
                    </Button>
                    <Link to='/facility/business-units'>
                      <Button
                        color='primary'
                        className='btn-cancel'
                        style={{ marginLeft: '5px' }}>
                        {t('common.buttons.cancel')}
                      </Button>
                    </Link>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}></GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </form>
    </div>
  );
}
